import type { AccessorFn } from '@tanstack/react-table'
import { formatSpecValue } from '@/lib/specs/labels'
import type { ProductCard } from '@convex/products/validators.lib'
import type { DataFormat } from "@/components/ui/record-table/column-types";
import { isUnknownRecord } from '@shared/validation/narrow'

type AccessoryProduct = ProductCard

export interface AccessoryColumnDefinition {
  id: string
  header: string
  accessor: AccessorFn<AccessoryProduct, unknown>
  sortable?: boolean
  filterable?: boolean
  format?: DataFormat
  align?: 'left' | 'center' | 'right'
  width?: number
  monospace?: boolean
}

function getSpecValue(specs: AccessoryProduct['specs'], key: string): string | null {
  const flatSpecs: unknown = specs
  if (!isUnknownRecord(flatSpecs) || !(key in flatSpecs)) return null
  return formatSpecValue(flatSpecs[key])
}

const COMMON_START_COLUMNS: AccessoryColumnDefinition[] = [
  {
    id: 'title',
    header: 'Product',
    accessor: (product) => product.title,
    sortable: false,
    width: 200,
  },
]

const COMMON_END_COLUMNS: AccessoryColumnDefinition[] = [
  {
    id: 'material',
    header: 'Material',
    accessor: (product) => getSpecValue(product.specs, 'material'),
    sortable: true,
  },
  {
    id: 'price',
    header: 'Price',
    accessor: (product) => product.buyerUnitPriceCents,
    sortable: true,
    align: 'right',
    width: 100,
    format: 'price',
  },
]

const ACCESSORY_SPEC_COLUMNS: Record<string, AccessoryColumnDefinition[]> = {
  'ibc-caps': [
    {
      id: 'diameter',
      header: 'Diameter',
      accessor: (product) => getSpecValue(product.specs, 'diameter'),
      sortable: true,
    },
    {
      id: 'inletConnection',
      header: 'Inlet connection',
      accessor: (product) => getSpecValue(product.specs, 'inletConnection'),
      sortable: true,
    },
    {
      id: 'color',
      header: 'Color',
      accessor: (product) => getSpecValue(product.specs, 'color'),
      sortable: false,
    },
  ],

  'ibc-valves': [
    {
      id: 'outletConnection',
      header: 'Outlet connection',
      accessor: (product) => getSpecValue(product.specs, 'outletConnection'),
      sortable: true,
    },
    {
      id: 'inletConnection',
      header: 'Inlet connection',
      accessor: (product) => getSpecValue(product.specs, 'inletConnection'),
      sortable: true,
    },
  ],

  'ibc-adapters': [
    {
      id: 'inletConnection',
      header: 'Inlet connection',
      accessor: (product) => getSpecValue(product.specs, 'inletConnection'),
      sortable: true,
    },
    {
      id: 'outletConnection',
      header: 'Outlet connection',
      accessor: (product) => getSpecValue(product.specs, 'outletConnection'),
      sortable: true,
    },
  ],

  'ibc-gaskets': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
  ],

  'ibc-stands': [
    {
      id: 'height',
      header: 'Height',
      accessor: (product) => getSpecValue(product.specs, 'height'),
      sortable: true,
    },
    {
      id: 'loadCapacity',
      header: 'Load capacity',
      accessor: (product) => getSpecValue(product.specs, 'loadCapacity'),
      sortable: true,
    },
  ],

  'ibc-heaters': [
    {
      id: 'wattage',
      header: 'Wattage',
      accessor: (product) => getSpecValue(product.specs, 'wattage'),
      sortable: true,
    },
    {
      id: 'voltage',
      header: 'Voltage',
      accessor: (product) => getSpecValue(product.specs, 'voltage'),
      sortable: true,
    },
    {
      id: 'temperatureRange',
      header: 'Temp range',
      accessor: (product) => getSpecValue(product.specs, 'temperatureRange'),
      sortable: false,
    },
  ],

  'ibc-liners': [
    {
      id: 'capacity',
      header: 'Capacity',
      accessor: (product) => getSpecValue(product.specs, 'capacity'),
      sortable: true,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
  ],

  'drum-pumps': [
    {
      id: 'pumpType',
      header: 'Type',
      accessor: (product) => getSpecValue(product.specs, 'pumpType'),
      sortable: true,
    },
    {
      id: 'flowRate',
      header: 'Flow rate',
      accessor: (product) => getSpecValue(product.specs, 'flowRate'),
      sortable: true,
    },
    {
      id: 'tubeLength',
      header: 'Tube length',
      accessor: (product) => getSpecValue(product.specs, 'tubeLength'),
      sortable: true,
    },
  ],

  'drum-faucets': [
    {
      id: 'threadSize',
      header: 'Thread size',
      accessor: (product) => getSpecValue(product.specs, 'threadSize'),
      sortable: true,
    },
  ],

  'drum-bungs': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
    {
      id: 'threadType',
      header: 'Thread type',
      accessor: (product) => getSpecValue(product.specs, 'threadType'),
      sortable: true,
    },
  ],

  'drum-liners': [
    {
      id: 'capacity',
      header: 'Capacity',
      accessor: (product) => getSpecValue(product.specs, 'capacity'),
      sortable: true,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
  ],

  'drum-covers': [
    {
      id: 'diameter',
      header: 'Diameter',
      accessor: (product) => getSpecValue(product.specs, 'diameter'),
      sortable: true,
    },
  ],

  'drum-heaters': [
    {
      id: 'wattage',
      header: 'Wattage',
      accessor: (product) => getSpecValue(product.specs, 'wattage'),
      sortable: true,
    },
    {
      id: 'voltage',
      header: 'Voltage',
      accessor: (product) => getSpecValue(product.specs, 'voltage'),
      sortable: true,
    },
  ],

  'drum-funnels': [
    {
      id: 'diameter',
      header: 'Diameter',
      accessor: (product) => getSpecValue(product.specs, 'diameter'),
      sortable: true,
    },
    {
      id: 'capacity',
      header: 'Capacity',
      accessor: (product) => getSpecValue(product.specs, 'capacity'),
      sortable: true,
    },
  ],

  'gaylord-liners': [
    {
      id: 'dimensions',
      header: 'W × gusset × H',
      accessor: (product) => getSpecValue(product.specs, 'dimensions'),
      sortable: false,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
    {
      id: 'qtyPerRoll',
      header: 'Qty/roll',
      accessor: (product) => getSpecValue(product.specs, 'qtyPerRoll'),
      sortable: true,
    },
  ],

  'gaylord-covers': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
    {
      id: 'qtyPerCase',
      header: 'Qty/case',
      accessor: (product) => getSpecValue(product.specs, 'qtyPerCase'),
      sortable: true,
    },
  ],

  'gaylord-lids': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
    {
      id: 'wallType',
      header: 'Wall type',
      accessor: (product) => getSpecValue(product.specs, 'wallType'),
      sortable: false,
    },
  ],

  'bulk-bag-liners': [
    {
      id: 'capacity',
      header: 'Capacity',
      accessor: (product) => getSpecValue(product.specs, 'capacity'),
      sortable: true,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
  ],

  'bulk-bag-lifters': [
    {
      id: 'loadCapacity',
      header: 'Load capacity',
      accessor: (product) => getSpecValue(product.specs, 'loadCapacity'),
      sortable: true,
    },
  ],

  'pallet-covers': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
    {
      id: 'thickness',
      header: 'Thickness',
      accessor: (product) => getSpecValue(product.specs, 'thickness'),
      sortable: true,
    },
  ],

  'pallet-corner-protectors': [
    {
      id: 'size',
      header: 'Size',
      accessor: (product) => getSpecValue(product.specs, 'size'),
      sortable: true,
    },
  ],
}

const DEFAULT_SPEC_COLUMNS: AccessoryColumnDefinition[] = [
  {
    id: 'condition',
    header: 'Condition',
    accessor: (product) => getSpecValue(product.specs, 'condition'),
    sortable: true,
  },
]

export function getAccessoryColumnDefinitions(
  accessoryTypeSlug: string
): AccessoryColumnDefinition[] {
  const specColumns =
    ACCESSORY_SPEC_COLUMNS[accessoryTypeSlug] ?? DEFAULT_SPEC_COLUMNS

  return [...COMMON_START_COLUMNS, ...specColumns, ...COMMON_END_COLUMNS]
}

export type { AccessoryProduct }
